import { showAlert } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import NetInfo, { type NetInfoState } from '@react-native-community/netinfo';

export const NETWORK_OFFLINE_CODE = 'network/offline';

export class NetworkOfflineError extends Error {
  readonly code = NETWORK_OFFLINE_CODE;

  readonly alertHandled = true;

  constructor() {
    super('No internet connection');
    this.name = 'NetworkOfflineError';
  }
}

let cachedIsConnected: boolean | null = null;
let offlineAlertVisible = false;

export function isStateOnline(state: NetInfoState): boolean {
  if (state.isConnected === false) {
    return false;
  }
  if (state.isInternetReachable === false) {
    return false;
  }
  return true;
}

/**
 * Subscribes to NetInfo and warms an in-memory `isConnected` cache so callers
 * can `await ensureOnline()` synchronously without an extra round-trip. Returns
 * the unsubscribe function — wire up at app mount.
 */
export function startNetworkStatusListener(): () => void {
  const unsubscribe = NetInfo.addEventListener(state => {
    const online = isStateOnline(state);
    cachedIsConnected = online;
  });
  NetInfo.fetch()
    .then(state => {
      cachedIsConnected = isStateOnline(state);
    })
    .catch(() => {});
  return () => {
    unsubscribe();
  };
}

export function showOfflineAlert(): void {
  if (offlineAlertVisible) {
    return;
  }
  offlineAlertVisible = true;
  showAlert({
    title: STRINGS.errors.noInternetTitle,
    message: STRINGS.errors.noInternetMessage,
    buttons: [
      {
        text: STRINGS.errors.ok,
        onPress: () => {
          offlineAlertVisible = false;
        },
      },
    ],
  });
}

export async function isOnline(): Promise<boolean> {
  if (cachedIsConnected !== null) {
    return cachedIsConnected;
  }
  try {
    const state = await NetInfo.fetch();
    cachedIsConnected = isStateOnline(state);
    return cachedIsConnected;
  } catch {
    return true;
  }
}

/**
 * Pre-flight check before any network request. Throws `NetworkOfflineError`
 * (with `alertHandled = true`) and surfaces the offline alert once if the
 * device is offline.
 */
export async function ensureOnline(): Promise<void> {
  const online = await isOnline();
  if (!online) {
    showOfflineAlert();
    throw new NetworkOfflineError();
  }
}

export function isNetworkOfflineError(error: unknown): boolean {
  if (error instanceof NetworkOfflineError) {
    return true;
  }
  return (error as { code?: string } | null)?.code === NETWORK_OFFLINE_CODE;
}

/**
 * Heuristic for axios errors that fail without a response — typically the
 * device lost connectivity mid-request.
 */
export function isAxiosNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const e = error as { code?: string; message?: string; response?: unknown };
  if (e.response) {
    return false;
  }
  if (e.code === 'ERR_NETWORK') {
    return true;
  }
  return typeof e.message === 'string' && /network error/i.test(e.message);
}
